/**
 * PERSO: ouverture des sous-menus du header au clic (réglage « Ouvrir les sous-menus au clic » du bloc Menu).
 *
 * Le lien parent pilote le bouton caché d'Horizon (assets/header-menu.js, méthode toggle) : ouverture,
 * fermeture, inert, hauteurs et aria-expanded du bouton restent gérés par Horizon. Ce script ajoute
 * seulement le clic sur le lien parent, la touche Espace, l'état aria-expanded du lien, la fermeture
 * au clic en dehors ou quand le focus quitte le menu, et le retour du focus sur le lien après Échap.
 * Chargé uniquement quand le réglage est coché. Le tiroir mobile n'est pas concerné.
 */

const SELECTEUR_PARENT = 'a[data-perso-parent]';
const SELECTEUR_BOUTON = '[ref="disclosure[]"]';

/**
 * @param {Element} lien
 * @returns {HTMLElement | null}
 */
function boutonDe(lien) {
  const bouton = lien.closest('.menu-list__list-item')?.querySelector(SELECTEUR_BOUTON);
  return bouton instanceof HTMLElement ? bouton : null;
}

/**
 * @param {EventTarget | null} cible
 * @returns {Element | null}
 */
function parentDe(cible) {
  return cible instanceof Element ? cible.closest(SELECTEUR_PARENT) : null;
}

/**
 * @param {HTMLElement} menu
 */
function initialiser(menu) {
  if (menu.dataset.persoClicPret !== undefined) return;
  menu.dataset.persoClicPret = '';

  /** @returns {HTMLElement | null} */
  const boutonOuvert = () => menu.querySelector(`${SELECTEUR_BOUTON}[aria-expanded="true"]`);

  /** Ferme le sous-menu ouvert par la méthode d'Horizon, sans déplacer le focus */
  const fermer = () => {
    const bouton = boutonOuvert();
    // @ts-ignore - toggle est une méthode publique du composant header-menu d'Horizon
    if (bouton && typeof menu.toggle === 'function') menu.toggle({ target: bouton });
  };

  // Clic, et Entrée (qui déclenche un clic sur un lien) : ouvre ou ferme au lieu de naviguer
  menu.addEventListener('click', (event) => {
    const lien = parentDe(event.target);
    if (!lien || !menu.contains(lien)) return;
    event.preventDefault();
    boutonDe(lien)?.click();
  });

  // Espace : comportement attendu d'un élément de rôle bouton
  menu.addEventListener('keydown', (event) => {
    if (event.key !== ' ') return;
    const lien = parentDe(event.target);
    if (!lien) return;
    event.preventDefault();
    boutonDe(lien)?.click();
  });

  // aria-expanded du lien = celui du bouton d'Horizon, quelle que soit la cause du changement
  for (const lien of menu.querySelectorAll(SELECTEUR_PARENT)) {
    const bouton = boutonDe(lien);
    if (!bouton) continue;
    const synchroniser = () => {
      lien.setAttribute('aria-expanded', bouton.getAttribute('aria-expanded') === 'true' ? 'true' : 'false');
    };
    new MutationObserver(synchroniser).observe(bouton, { attributes: true, attributeFilter: ['aria-expanded'] });
    synchroniser();
  }

  // Fermeture au clic en dehors du menu
  document.addEventListener('click', (event) => {
    if (event.target instanceof Node && menu.contains(event.target)) return;
    fermer();
  });

  // Fermeture quand le focus quitte le menu au clavier (un clic dans le panneau ne ferme pas)
  menu.addEventListener('focusout', (event) => {
    const cible = event.relatedTarget;
    if (!(cible instanceof Node) || menu.contains(cible)) return;
    // Un tiroir ouvert (panier, chat) installe un piege a focus : des que le focus se pose
    // ailleurs dans la page, focus.js le rappelle dans le tiroir. Ce n'est pas le visiteur qui
    // quitte le menu. Sans cette garde, l'appui sur une categorie fermait le sous-menu avant le
    // relachement, et le clic n'atteignait jamais le lien : le menu se fermait au lieu de naviguer.
    if (cible instanceof Element && cible.closest('dialog[open]')) return;
    fermer();
  });

  // Après Échap, Horizon rend le focus au bouton caché (hors tabulation) : on le rend au lien parent
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const actif = document.activeElement;
    if (!(actif instanceof HTMLElement) || !menu.contains(actif) || !actif.matches(SELECTEUR_BOUTON)) return;
    const lien = actif.closest('.menu-list__list-item')?.querySelector(SELECTEUR_PARENT);
    if (lien instanceof HTMLElement) lien.focus();
  });
}

function initialiserTout() {
  for (const menu of document.querySelectorAll('header-menu[data-perso-clic]')) {
    if (menu instanceof HTMLElement) initialiser(menu);
  }
}

initialiserTout();

// Éditeur de thème : la section du header est rechargée à chaque modification
document.addEventListener('shopify:section:load', initialiserTout);
